import { useState, type CSSProperties } from 'react';
import type { CommentResolution, CommentVisibility, ContractCommentView } from '../api';
import { IconEye, IconLock } from '../components/icons';
import { initials } from '../lib/kb';

/*
 * Ветка замечаний к договору — пузырь на полях документа (3.56).
 *
 * Две части нарочно разведены. `NoteBody` — содержимое ветки: цитата, автор,
 * текст, предложенная формулировка, ответы и подвал с действиями. `Note` —
 * тот же состав в карточке `.doc-note`, поставленной на высоте своего
 * выделения. На узком экране полей нет, и ветка открывается нижним листом с
 * теми же частями, но без карточки: `.doc-note` спозиционирован абсолютно и
 * внутри окна не живёт.
 *
 * Права приходят готовыми, экран их не выводит: видимость меняет автор ветки
 * или держатель, ответ по существу — только держатель (правила в
 * server/src/contracts/comments.service.ts).
 */

export type NoteActions = {
  /** Можно ли вообще писать: договор в комментировании и есть этап. */
  canComment: boolean;
  /** Показывать ли круг видимости: у гостя он всегда один, и метка лишняя. */
  showVisibility: boolean;
  canVisibility: boolean;
  canResolve: boolean;
  busy: boolean;
  onReply: (text: string) => void;
  onVisibility: (v: CommentVisibility) => void;
  onResolve: (r: CommentResolution, note: string) => void;
};

type BodyProps = {
  thread: ContractCommentView;
  /** «С. 1 · П. 2.1» — страница листа и пункт из слепка. */
  address: string;
  actions: NoteActions;
};

const ts = (iso: string): string =>
  new Date(iso).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

/** Кто написал: у гостя вместо должности компания и свой тон аватара. */
function Who({ c }: { c: ContractCommentView }) {
  const a = c.author;
  const guest = 'guest' in a;
  const name = 'guest' in a ? a.guest : a.name;
  const sub = 'guest' in a ? a.company : a.title;
  return (
    <div className="doc-who">
      <span className={guest ? 'avatar xs ext' : 'avatar xs'}>{initials(name)}</span>
      <b>{name}</b>
      {!!sub && <i>{sub}</i>}
      <span className="cm-ts">{ts(c.createdAt)}</span>
    </div>
  );
}

export function NoteBody({ thread, address, actions }: BodyProps) {
  const [reply, setReply] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const shared = thread.visibility === 'SHARED';
  // Ветка УК (часть 4): гость УК и сотрудники, контрагент её не видит.
  // Видимость такой ветки не переключается — держатель не заводит и не
  // снимает её сам.
  const uk = thread.visibility === 'UK';
  const guestThread = 'guest' in thread.author;

  return (
    <>
      <div className="doc-q">
        <b>{address}</b> «{thread.quote}»
      </div>
      <Who c={thread} />
      <div className="doc-text">{thread.text}</div>
      {!!thread.proposal && (
        <div className="doc-prop">
          <b>Формулировка</b>
          {thread.proposal}
        </div>
      )}
      {thread.replies.map((r) => (
        <div className="doc-reply" key={r.id}>
          <Who c={r} />
          <div className="doc-text">{r.text}</div>
        </div>
      ))}
      {!!thread.resolutionNote && <div className="doc-text">{thread.resolutionNote}</div>}

      <div className="doc-foot">
        {thread.anchorState === 'MOVED' && <span className="status crit sm">Место изменено</span>}
        {/* Номера версии здесь нет нарочно: перенос ветки на новую версию
            переписывает `versionId`, и «учтено в версии N» всегда называло бы
            последний круг, а не тот, в котором держатель ответил. */}
        {thread.resolution === 'ACCEPTED' && <span className="status ok sm">Учтено</span>}
        {thread.resolution === 'REJECTED' && <span className="status crit sm">Отклонено</span>}
        {actions.showVisibility && (
          <span className={shared || uk ? 'doc-vis ext' : 'doc-vis'}>
            {shared || uk ? <IconEye /> : <IconLock />}
            {shared ? 'Видит подрядчик' : uk ? 'Видит УК' : 'Внутренний'}
          </span>
        )}
        <span className="grow" />
        {actions.canVisibility && !guestThread && !uk && (
          <button
            className="btn link sm quiet"
            type="button"
            disabled={actions.busy}
            onClick={() => actions.onVisibility(shared ? 'INTERNAL' : 'SHARED')}
          >
            {shared ? 'Скрыть от подрядчика' : 'Показать подрядчику'}
          </button>
        )}
        {actions.canResolve && thread.resolution === 'NONE' && (
          <>
            <button
              className="btn link sm"
              type="button"
              disabled={actions.busy}
              onClick={() => actions.onResolve('ACCEPTED', '')}
            >
              Учтено
            </button>
            <button
              className="btn link sm quiet"
              type="button"
              disabled={actions.busy}
              onClick={() => setNote(note === null ? '' : null)}
            >
              Отклонено
            </button>
          </>
        )}
        {actions.canComment && (
          <button className="btn link sm" type="button" onClick={() => setReply(reply === null ? '' : null)}>
            Ответить
          </button>
        )}
      </div>

      {note !== null && (
        <div className="doc-reply">
          <input
            className="input"
            value={note}
            autoFocus
            placeholder="Почему замечание отклонено"
            onChange={(e) => setNote(e.target.value)}
          />
          <div className="doc-foot">
            <span className="grow" />
            <button className="btn link sm quiet" type="button" onClick={() => setNote(null)}>
              Отмена
            </button>
            <button
              className="btn link sm"
              type="button"
              disabled={actions.busy || !note.trim()}
              onClick={() => {
                actions.onResolve('REJECTED', note.trim());
                setNote(null);
              }}
            >
              Отправить
            </button>
          </div>
        </div>
      )}

      {reply !== null && (
        <div className="doc-reply">
          <textarea
            className="textarea"
            value={reply}
            autoFocus
            placeholder="Ответ в ветке"
            onChange={(e) => setReply(e.target.value)}
          />
          <div className="doc-foot">
            <span className="grow" />
            <button className="btn link sm quiet" type="button" onClick={() => setReply(null)}>
              Отмена
            </button>
            <button
              className="btn link sm"
              type="button"
              disabled={actions.busy || !reply.trim()}
              onClick={() => {
                actions.onReply(reply.trim());
                setReply(null);
              }}
            >
              Отправить
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export function Note({
  thread,
  address,
  actions,
  selected,
  top,
  pushed,
  push,
  onSelect,
}: BodyProps & {
  selected: boolean;
  /** Вычисляемая геометрия: высота пузыря на поле и длина сломанной выноски. */
  top: number;
  pushed: boolean;
  push: number;
  onSelect: () => void;
}) {
  const done = thread.resolution !== 'NONE';
  const style = { top, '--doc-push': `${Math.round(push)}px` } as CSSProperties;
  return (
    <div
      className={`doc-note${selected ? ' on' : ''}${done ? ' done' : ''}${pushed ? ' pushed' : ''}`}
      style={style}
      onClick={onSelect}
    >
      <NoteBody thread={thread} address={address} actions={actions} />
    </div>
  );
}
